import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";
import {
  Alert,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Card as MuiCard,
  CardContent,
  Chip,
  CircularProgress,
  Divider as MuiDivider,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import { Box, spacing } from "@mui/system";
import { format } from "date-fns";
import axiosInstance from "../../utils/axios";
import CustomSnackbar from "./SnackBar";
import useSnackbar from "./SnackbarHook";

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

/**
 * Payments read from the operator's own bank account by the daily job.
 *
 * The unmatched list is the working one: the job settles anything whose
 * reference and amount agree exactly, and leaves everything else here with the
 * reason it could not. Where it can name the invoice a payment appears to
 * concern, that becomes a button — otherwise the flag is just prose to go
 * hunting from, which is what this replaces.
 */

type Payment = {
  id: string;
  bookingDate: string;
  amountCents: number;
  currency: string;
  payer: string | null;
  referenceNumber: string | null;
  status: string;
  reason: string | null;
  subscriptionInvoiceId: number | null;
  invoiceNumber: string | null;
  invoiceStatus: string | null;
  associationName: string | null;
};

const eur = (cents: number) =>
  new Intl.NumberFormat("fi-FI", {
    style: "currency",
    currency: "EUR",
  }).format((cents ?? 0) / 100);

function SuperadminSubscriptionPayments() {
  const [tab, setTab] = useState<"unmatched" | "matched">("unmatched");
  const [rows, setRows] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    progress,
    duration,
  } = useSnackbar();

  const notify = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/subscription-payments", {
        params: { status: tab },
      });
      setRows(data ?? []);
    } catch (error: any) {
      notify(
        error?.response?.data?.message ?? "Maksujen haku epäonnistui.",
        "error"
      );
    } finally {
      setLoading(false);
    }
    // notify is stable enough for this page; re-running on tab is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Settling goes through the same endpoint a person uses by hand, so there is
   * one code path that marks an invoice paid rather than two that can drift.
   */
  const markPaid = async (payment: Payment) => {
    if (!payment.subscriptionInvoiceId) return;
    if (
      !window.confirm(
        `Merkitäänkö lasku ${
          payment.invoiceNumber ?? payment.subscriptionInvoiceId
        } maksetuksi?\n\n` +
          "Tilauskausi siirtyy eteenpäin ja tilaus aktivoituu."
      )
    ) {
      return;
    }
    setBusyId(payment.subscriptionInvoiceId);
    try {
      await axiosInstance.put(
        `/subscription-invoices/${payment.subscriptionInvoiceId}/mark-paid`
      );
      notify("Lasku merkitty maksetuksi.", "success");
      await load();
    } catch (error: any) {
      notify(
        error?.response?.data?.message ?? "Laskun merkitseminen epäonnistui.",
        "error"
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <React.Fragment>
      <Helmet title="Tilausmaksut" />
      <Typography variant="h3" gutterBottom display="inline">
        Tilausmaksut
      </Typography>
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Tilausmaksut</Typography>
      </Breadcrumbs>
      <Divider my={6} />

      <Card mb={6}>
        <CardContent>
          <Alert severity="info" sx={{ mb: 4 }}>
            Pankkitilin tapahtumat haetaan kerran vuorokaudessa. Maksut, joiden
            viitenumero ja summa täsmäävät avoimeen laskuun, merkitään
            maksetuiksi automaattisesti. Loput näkyvät tässä syineen.
          </Alert>

          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            sx={{ mb: 3 }}
          >
            <Tab label="Selvitettävät" value="unmatched" />
            <Tab label="Kohdistetut" value="matched" />
          </Tabs>

          {loading ? (
            <Box display="flex" justifyContent="center" p={6}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Alert severity="success">
              {tab === "unmatched"
                ? "Ei selvitettäviä maksuja."
                : "Ei vielä kohdistettuja maksuja."}
            </Alert>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Pvm</TableCell>
                    <TableCell>Maksaja</TableCell>
                    <TableCell align="right">Summa</TableCell>
                    <TableCell>Viite</TableCell>
                    <TableCell>
                      {tab === "unmatched" ? "Syy" : "Lasku"}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>
                        {format(new Date(payment.bookingDate), "dd.MM.yyyy")}
                      </TableCell>
                      <TableCell>{payment.payer ?? "—"}</TableCell>
                      <TableCell align="right">
                        {eur(payment.amountCents)}
                      </TableCell>
                      <TableCell>{payment.referenceNumber ?? "—"}</TableCell>
                      <TableCell>
                        {tab === "unmatched" ? (
                          payment.reason
                        ) : (
                          <>
                            {payment.invoiceNumber ?? "—"}
                            {payment.associationName ? (
                              <Typography variant="caption" display="block">
                                {payment.associationName}
                              </Typography>
                            ) : null}
                          </>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {tab === "unmatched" &&
                        payment.subscriptionInvoiceId &&
                        payment.invoiceStatus !== "paid" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busyId === payment.subscriptionInvoiceId}
                            onClick={() => markPaid(payment)}
                          >
                            Merkitse lasku maksetuksi
                          </Button>
                        ) : tab === "matched" ? (
                          <Chip size="small" label="Maksettu" color="success" />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </CardContent>
      </Card>

      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={() => setOpenSnackbar(false)}
      />
    </React.Fragment>
  );
}

export default SuperadminSubscriptionPayments;
